import { useState } from "react";
import { Button } from "../../components/Button";
import type { SelfMark, TheoryQuestion } from "../../lib/practice";
import { QuestionText } from "../../components/QuestionText";
import { TheoryAnswerInput, useAnswerCheck } from "../../components/theoryEditor/TheoryAnswerInput";

export interface TheoryMark {
  score: number;
  maxScore: number;
  feedback: string;
  overallFeedback: string;
  /** False in v1 — the mark is feedback, not a tracking input (ADR-0006). */
  readinessAffected: boolean;
}

interface TheoryQuestionScreenProps {
  question: TheoryQuestion;
  onNext: (mark: SelfMark) => void;
  onAskTutor?: (() => void) | undefined;
  offline?: boolean | undefined;
  /**
   * Send the answer to the marker.
   *
   * Absent when the subject has no WAEC marking scheme, or when there is no
   * network — in which case the screen falls back to the guide and the
   * student's own honest read of it.
   */
  onMark?: ((answer: string) => void) | undefined;
  marking?: boolean | undefined;
  marked?: TheoryMark | null | undefined;
  markError?: string | null | undefined;
  onClose?: (() => void) | undefined;
}

const MARKS: { id: SelfMark; label: string }[] = [
  { id: "all", label: "Got all 3" },
  { id: "partial", label: "Got 1–2" },
  { id: "missed", label: "Missed it" },
];

/**
 * Screen 9b — WAEC theory: written, then marked.
 *
 * The marking guide stays hidden until an answer has been written. Revealed
 * first, it becomes something to copy from and the comparison measures
 * nothing.
 *
 * When the subject has a WAEC marking scheme the answer is marked by the
 * examiner-trained model and the student reads a real score and real feedback.
 * The self-rate stays underneath it, because ADR-0006 keeps AI marks out of
 * readiness until the eval gate exists — and a student is owed the truth about
 * which of the two numbers moves anything.
 */
export function TheoryQuestionScreen({
  question,
  onNext,
  onAskTutor,
  offline = false,
  onMark,
  marking = false,
  marked = null,
  markError = null,
  onClose,
}: TheoryQuestionScreenProps) {
  const [answer, setAnswer] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [mark, setMark] = useState<SelfMark | null>(null);

  /*
   * A new question is a clean sheet.
   *
   * The route advances by swapping the `question` prop on the same component,
   * so all of this survived: question 2 opened with question 1's answer still
   * in the box and `revealed` still true — which disables the textarea. A
   * student who reached a second theory question simply could not type.
   */
  const [shownQuestionId, setShownQuestionId] = useState(question.id);
  if (shownQuestionId !== question.id) {
    setShownQuestionId(question.id);
    setAnswer("");
    setRevealed(false);
    setMark(null);
  }

  const written = answer.trim().length > 0;
  const check = useAnswerCheck(answer);
  const showGuide = revealed || marked !== null;

  return (
    <div className="app-column px-5 pb-6 pt-4">
      <header className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              aria-label="Leave this session"
              className="-ml-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-control)] text-[18px] leading-none text-text-muted"
            >
              ✕
            </button>
          ) : null}
          <p className="m-0 truncate text-[12px] text-text-muted">
            {question.subject} · {question.source}
          </p>
        </div>
        <span className="tabular shrink-0 text-[12px] text-text-muted">
          Q{question.index} of {question.total}
        </span>
      </header>

      <div className="flex items-start justify-between gap-3 pt-5">
        <QuestionText as="h1" className="m-0 text-[17px] font-semibold leading-[1.45]">
          {question.stem}
        </QuestionText>
        <span className="tabular shrink-0 rounded-[var(--radius-chip)] border border-border bg-surface-high px-2.5 py-1 text-[11px] font-semibold">
          {question.marks} marks
        </span>
      </div>

      <label
        htmlFor="theory-answer"
        className="pt-5 text-[11px] font-semibold uppercase tracking-[0.1em] text-text-muted"
      >
        Your answer
      </label>
      <TheoryAnswerInput
        questionId={question.id}
        subject={question.subject}
        value={answer}
        // Locked only once a mark has come back — before that, a student who
        // reveals the guide may still want to keep writing.
        disabled={marked !== null || marking}
        marked={marked !== null}
        onChange={setAnswer}
      />
      {written && check.problem ? (
        <p role="status" className="m-0 mt-2 text-[12px] leading-[1.45] text-negative">
          {check.problem}
        </p>
      ) : null}

      {markError ? (
        <p
          role="alert"
          className="m-0 mt-3 rounded-[var(--radius-control)] border border-border bg-surface px-3.5 py-3 text-[12.5px] text-negative"
        >
          {markError}
        </p>
      ) : null}

      {marked ? (
        <section className="mt-5 flex flex-col gap-2 rounded-[var(--radius-card)] border border-accent bg-accent-tint p-4">
          <p className="tabular m-0 font-[family-name:var(--font-display)] text-[22px] font-bold leading-none">
            {marked.score} / {marked.maxScore}
          </p>
          <p className="m-0 text-[13.5px] leading-[1.6]">{marked.feedback}</p>
          {marked.overallFeedback && marked.overallFeedback !== marked.feedback ? (
            <p className="m-0 text-[12.5px] leading-[1.55] text-text-muted">
              {marked.overallFeedback}
            </p>
          ) : null}
          {!marked.readinessAffected ? (
            /*
             * Said out loud, because the alternative is a student assuming a
             * mark they can see moved a number they cannot.
             */
            <p className="m-0 pt-1 text-[11.5px] leading-[1.45] text-text-muted">
              This mark is for your review. WAEC theory doesn't move your readiness yet — only
              questions we can mark objectively do.
            </p>
          ) : null}
        </section>
      ) : null}

      {!showGuide ? (
        <div className="flex flex-col gap-2 pt-4">
          {onMark ? (
            <Button
              disabled={!written || marking || check.problem !== null}
              onClick={() => onMark(answer)}
            >
              {marking ? "Marking…" : "Mark my answer"}
            </Button>
          ) : null}
          <Button
            variant={onMark ? "secondary" : "primary"}
            disabled={!written || marking}
            onClick={() => setRevealed(true)}
          >
            Show the marking guide
          </Button>
          {!onMark && !offline ? (
            <p className="m-0 text-[11.5px] leading-[1.45] text-text-muted">
              We can't mark this subject's theory yet — compare your answer against the guide.
            </p>
          ) : null}
          {offline ? (
            <p className="m-0 text-[11.5px] leading-[1.45] text-text-muted">
              Marking needs internet — the guide doesn't.
            </p>
          ) : null}
        </div>
      ) : (
        <>
          <section className="mt-5 flex flex-col gap-3 rounded-[var(--radius-card)] border border-border bg-surface p-4">
            <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.1em] text-text-muted">
              Marking guide — {question.guidance}
            </p>
            <ol className="m-0 flex list-none flex-col gap-2 p-0">
              {question.points.map((point, index) => (
                <li key={point} className="flex gap-2.5 text-[13.5px] leading-[1.5]">
                  <span className="tabular shrink-0 font-semibold text-text-muted">
                    {index + 1}.
                  </span>
                  <QuestionText>{point}</QuestionText>
                </li>
              ))}
            </ol>
          </section>

          <fieldset className="m-0 mt-5 border-0 p-0">
            <legend className="p-0 text-[13px] text-text-muted">
              {marked
                ? "And your own read of it?"
                : "How did you do? Be honest — it's your own review."}
            </legend>
            <div className="flex gap-2 pt-3">
              {MARKS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  aria-pressed={mark === option.id}
                  onClick={() => setMark(option.id)}
                  className={`min-h-11 flex-1 rounded-[var(--radius-chip)] border px-2 text-[12.5px] font-semibold transition-colors
                    ${mark === option.id ? "border-accent bg-accent-tint" : "border-border bg-surface text-text-muted"}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </fieldset>

          <div className="mt-5 flex gap-2">
            {offline ? (
              <p className="m-0 flex-1 text-[12px] leading-[1.45] text-text-muted">
                Tutor needs internet — your practice doesn't.
              </p>
            ) : (
              <Button variant="secondary" className="text-[13px]" onClick={onAskTutor}>
                Ask the tutor
              </Button>
            )}
            <Button
              className="text-[13px]"
              disabled={mark === null}
              onClick={() => mark && onNext(mark)}
            >
              Next question
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
